import React, { useEffect, useRef, useState } from 'react';
import { Modal, TextInput } from '@design-system';
import { Button } from '@design-system';
import SignatureCanvas from 'react-signature-canvas';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { Trans, useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useMobileDetect } from 'hooks/useIsMobile';
import classNames from 'classnames';
import { cn, TextSignature } from './text-signature';
import { LegalLegend } from './LegalLegend';
import { WLContext } from 'contexts/WLContext/WLContext';
import { AdvancedSignature } from './SignatureTabs/components/AdvancedSignature';
// @ts-ignore
import meta from '../../../meta.json';

const versionTag = `${(meta as any).tag}+${(meta as any).distance}`;


// Canvas aspect ratio: 800:492 (≈ 200:123)
const CANVAS_W = 800;
const CANVAS_H = 492;

const validateUsername = (username: string): boolean => {
  const normalized = username.replace(/[-\s]+/g, ' ').trim();
  const parts = normalized.split(' ');
  if (parts.length < 2) return false;
  return parts.filter((p) => p.length >= 2).length >= 2;
};

export const SignatureModal = ({
  borderColor,
  showModal,
  setSignatureModal,
  onSuccess,
  isLoading,
  isDisabled,
  canvasWidth: _canvasWidth,
  signatureRef,
  handleMouseUp,
  handleTouchEnd,
  user,
  currentSigner,
  isGreaterThan20Mb = false,
  currentInvitation,
  handleCertificateFile,
  handleKeyFile,
  handlePassword,
}) => {
  const username = (user?.name || currentSigner?.name) ?? '';
  const { color } = React.useContext(WLContext);
  const [value, setValue] = React.useState(username);
  const isValid = React.useMemo(() => validateUsername(value), [value]);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [showMessage, setShowMessage] = useState(false);
  const { isMobile } = useMobileDetect();

  const [tab, setTab] = useState<'advanced' | 'free-draw' | 'text'>('free-draw');
  const activeStyle = { borderColor: color };

  const availableTypes = React.useMemo(() => {
    const types = currentInvitation?.signature_allowed_types?.split(',') || [];
    return {
      hasAdvanced: types.includes('advanced'),
      hasAutograph: types.includes('autograph'),
      hasTyped: types.includes('name'),
    };
  }, [currentInvitation?.signature_allowed_types]);

  React.useEffect(() => {
    if (availableTypes.hasAutograph) setTab('free-draw');
    else if (availableTypes.hasAdvanced) setTab('advanced');
    else if (availableTypes.hasTyped) setTab('text');
  }, [availableTypes]);

  React.useEffect(() => {
    if (username && username !== '') setValue(username);
  }, [username]);

  useEffect(() => {
    if (ref?.current && showModal) disableBodyScroll(ref.current);
    if (ref?.current && !showModal) enableBodyScroll(ref.current);
    return () => clearAllBodyScrollLocks();
  }, [ref, showModal]);

  // Hide the global PoweredByCincel badge while signing modal is open
  useEffect(() => {
    const el = document.getElementById('powered-by-cincel');
    if (!el) return;
    if (showModal) el.style.display = 'none';
    return () => { el.style.display = ''; };
  }, [showModal]);

  // ── Canvas measurement ──────────────────────────────────────────
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = canvasContainerRef.current;
    if (!el) return;
    const measure = (width: number) => {
      if (width > 0) {
        setCanvasSize({
          w: Math.round(width),
          h: Math.round(width * CANVAS_H / CANVAS_W),
        });
      }
    };
    measure(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => measure(entries[0].contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [tab, showModal]);

  // ── Handlers ────────────────────────────────────────────────────
  const handleOnSign = () => {
    if (tab === 'text' && !isValid) return;
    if (isGreaterThan20Mb) setShowMessage(true);
    const signatureType = tab === 'text' ? 'name' : tab === 'advanced' ? 'advanced' : 'autograph';
    return onSuccess(signatureType);
  };

  const handleClear = () => {
    signatureRef?.current?.clear?.();
    setInkRatio(0);
  };

  const showTabs = [availableTypes.hasAdvanced, availableTypes.hasAutograph, availableTypes.hasTyped].filter(Boolean).length > 1;

  // ── Ink Ratio Calculation ───────────────────────────────────────
  const [inkRatio, setInkRatio] = useState(0);
  const EMPTY_THRESHOLD = 0.0021;

  const calculateRatio = () => {
    let canvas: HTMLCanvasElement | null = null;
    const currentRef = signatureRef?.current;
    if (!currentRef) return;

    try {
      if (tab === 'free-draw' && 'getCanvas' in currentRef) {
        // react-signature-canvas instance
        canvas = (currentRef as any).getCanvas();
      } else if (tab === 'text' && currentRef instanceof HTMLCanvasElement) {
        // TextSignature ref
        canvas = currentRef;
      }

      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { width, height } = canvas;
      // Read 1x1 pixels or full image data? Full image is safer for accuracy.
      const data = ctx.getImageData(0, 0, width, height).data;
      let filled = 0;
      // Alpha channel is every 4th byte
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 0) filled++;
      }
      setInkRatio(filled / (width * height));
    } catch (e) {
      // noop - might fail if canvas is tainted or context missing
    }
  };

  // Reset ratio on tab change
  useEffect(() => { setInkRatio(0); }, [tab]);

  // Recalculate text ratio when value changes (after render delay)
  useEffect(() => {
    if (tab === 'text') {
      const t = setTimeout(calculateRatio, 150);
      return () => clearTimeout(t);
    }
  }, [value, tab]);

  const isDrawEmpty = inkRatio < EMPTY_THRESHOLD;
  const isTextEmpty = inkRatio < EMPTY_THRESHOLD || !isValid;

  // ── Inline action buttons ───────────────────────────────────────
  const ActionButtons = () => (
    <div className="flex items-center gap-3 mt-auto pt-2">
      <div className="ml-auto w-full">
        <Button color={color} className="w-full" variant="primary"
          isLoading={isLoading} isDisabled={isDisabled || isLoading}
          size="sm" onClick={handleOnSign}>
          Firmar
        </Button>
      </div>
    </div>
  );

  // ── Vertical tab switcher (for 2-column / landscape mode) ──────
  const VerticalTabs = () => (
    <div className="flex flex-col gap-1 mb-3 border-b border-gray-100 pb-2">
      {availableTypes.hasAdvanced && (
        <button
          className={cn(
            'text-left text-xs font-medium py-1.5 px-2 rounded-md border-l-2 transition-colors',
            tab === 'advanced' ? 'bg-gray-50 border-current' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
          style={tab === 'advanced' ? { color } : undefined}
          onClick={() => setTab('advanced')}>
          {t(translations.COMMON.ADVANCED, { defaultValue: 'Avanzada' })}
        </button>
      )}
      {availableTypes.hasAutograph && (
        <button
          className={cn(
            'text-left text-xs font-medium py-1.5 px-2 rounded-md border-l-2 transition-colors',
            tab === 'free-draw' ? 'bg-gray-50 border-current' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
          style={tab === 'free-draw' ? { color } : undefined}
          onClick={() => setTab('free-draw')}>
          {t(translations.COMMON.AUTOGRAPH, { defaultValue: 'Autógrafa' })}
        </button>
      )}
      {availableTypes.hasTyped && (
        <button
          className={cn(
            'text-left text-xs font-medium py-1.5 px-2 rounded-md border-l-2 transition-colors',
            tab === 'text' ? 'bg-gray-50 border-current' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
          style={tab === 'text' ? { color } : undefined}
          onClick={() => setTab('text')}>
          {t(translations.COMMON.TYPED, { defaultValue: 'Nombre' })}
        </button>
      )}
    </div>
  );

  const HorizontalTabs = () => (
    <div className="mb-3 flex flex-row border-b border-gray-100">
      {availableTypes.hasAdvanced && (
        <Tab style={{ ...(tab === 'advanced' && activeStyle) }}
          onClick={() => setTab('advanced')}>
          {t(translations.COMMON.ADVANCED, { defaultValue: 'Avanzada' })}
        </Tab>
      )}
      {availableTypes.hasAutograph && (
        <Tab style={{ ...(tab === 'free-draw' && activeStyle) }}
          onClick={() => setTab('free-draw')}>
          {t(translations.COMMON.AUTOGRAPH, { defaultValue: 'Autógrafa' })}
        </Tab>
      )}
      {availableTypes.hasTyped && (
        <Tab style={{ ...(tab === 'text' && activeStyle) }}
          onClick={() => setTab('text')}>
          {t(translations.COMMON.TYPED, { defaultValue: 'Nombre' })}
        </Tab>
      )}
    </div>
  );

  // ── Right column content (varies by tab) ────────────────────────
  const rightColumnContent = (
    <div className="flex flex-col landscape:flex-[1] lg:flex-[1] mt-3 landscape:mt-0 lg:mt-0 min-w-0">
      {/* Vertical tabs: only in 2-column mode */}
      {showTabs && (
        <div className="hidden landscape:block lg:block">
          <VerticalTabs />
        </div>
      )}

      {/* Tab-specific info */}
      {tab === 'advanced' && (
        <>
          <LegalLegend i18nKey={translations.COMMON.EFIRMA_SIGNATURE_LEGEND}>
            Al dar clic en el botón <strong>Firmar </strong>manifiestas tu
            consentimiento para que se reflejen en el documento los datos
            generales de tu certificado digital (clave pública) así como su
            vigencia.
          </LegalLegend>
          <ActionButtons />
        </>
      )}

      {tab === 'free-draw' && (
        <>
          <LegalLegend i18nKey={translations.COMMON.AUTOGRAPH_SIGNATURE_LEGEND}>
            Al dar clic en el botón <strong>Firmar</strong>, confirmas tu
            consentimiento para utilizar el grafo trazado como tu firma
            autógrafa.
          </LegalLegend>
          <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">
            Firmante
          </p>
          <p className="text-xs text-gray-700 font-semibold truncate mb-3">
            {username}
          </p>
          <ActionButtons />
        </>
      )}

      {tab === 'text' && (
        <>
          <LegalLegend i18nKey={translations.COMMON.TYPED_SIGNATURE_LEGEND}>
            Al dar clic en el botón <strong>Firmar</strong>, confirmas tu
            consentimiento para utilizar el nombre completo que ingreses
            como tu firma autógrafa, equivalente a trazar un grafo.
          </LegalLegend>
          <ActionButtons />
        </>
      )}
    </div>
  );

  // ── Two-column layout class ─────────────────────────────────────
  const twoColClass = 'flex flex-col landscape:flex-row landscape:gap-4 lg:flex-row lg:gap-5';

  return (
    <Modal
      ref={ref}
      showModal={showModal}
      setShowModal={() => setSignatureModal((prev) => !prev)}
      onSuccess={handleOnSign}
      closeOnClickOutside
      closeOnSuccess={false}
      width="w-[95vw] landscape:w-[96vw] landscape:max-w-[680px] md:w-[80vw] lg:max-w-[560px] xl:max-w-[600px]"
      className="landscape:!p-3 landscape:!pb-1"
      showButtons={false}
      showCancel={false}
    >
      {/* Horizontal tabs: only visible in 1-column mode (portrait, below lg) */}
      {showTabs && (
        <div className="landscape:hidden lg:hidden">
          <HorizontalTabs />
        </div>
      )}

      {/* ========== e.Firma ========== */}
      {tab === 'advanced' && availableTypes.hasAdvanced && (
        <div className={twoColClass}>
          <div className="landscape:flex-[3] lg:flex-[3] min-w-0 flex flex-col justify-center">
            <AdvancedSignature
              handleCertificateFile={handleCertificateFile}
              handleKeyFile={handleKeyFile}
              handlePassword={handlePassword}
              onSign={handleOnSign}
              isLoading={isLoading}
              primaryColor={color}
              isDisabled={isDisabled}
              hideButton
              compact
            />
          </div>
          {rightColumnContent}
        </div>
      )}

      {/* ========== Firma con Trazo ========== */}
      {tab === 'free-draw' && availableTypes.hasAutograph && (
        <div className={twoColClass}>
          <div className="landscape:flex-[3] lg:flex-[3] min-w-0 flex items-center justify-center">
            <div
              ref={canvasContainerRef}
              onMouseUp={handleMouseUp}
              onTouchEnd={handleTouchEnd}
              className="w-full relative"
              style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}
            >
              {canvasSize && (
                <SignatureCanvas
                  ref={signatureRef}
                  onEnd={calculateRatio}
                  penColor="black"
                  canvasProps={{
                    className: classNames('sigCanvas border-b-2 bg-white shadow-sm rounded-lg', {
                      borderColor,
                    }),
                    style: { borderColor },
                    width: canvasSize.w,
                    height: canvasSize.h,
                  }}
                />
              )}
              {/* Clear button — top-right corner */}
              <button
                type="button"
                onClick={handleClear}
                disabled={isLoading}
                className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 hover:bg-gray-100 text-gray-400 hover:text-gray-600 shadow-sm border border-gray-200 transition-colors disabled:opacity-40"
                title="Borrar trazo"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              {/* Unified Debug Info Line */}
              <div className="absolute bottom-1.5 left-2 pointer-events-none select-none text-[8px] font-mono tabular-nums text-gray-300 flex items-center gap-1 opacity-70">
                <span>Powered by Cincel | {versionTag}</span>
                <span>-</span>
                <span>{canvasSize ? `${canvasSize.w}:${canvasSize.h}` : '0:0'}</span>
                <span>-</span>
                <span>r {inkRatio.toFixed(4)}</span>
                <span>-</span>
                <span className={isDrawEmpty ? "text-amber-500" : "text-green-500"}>
                  {isDrawEmpty ? 'nil' : 'set'}
                </span>
              </div>
            </div>
          </div>
          {rightColumnContent}
        </div>
      )}

      {/* ========== Firma con Nombre ========== */}
      {tab === 'text' && availableTypes.hasTyped && (
        <div className={twoColClass}>
          <div className="landscape:flex-[3] lg:flex-[3] min-w-0 flex flex-col items-center justify-center">
            <div className="w-full relative shadow-sm rounded-lg overflow-hidden border border-gray-100" style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}>
              <TextSignature
                ref={signatureRef}
                value={value}
                className="border-b-2 bg-white shadow-sm rounded-lg"
                style={{
                  ...activeStyle,
                  width: '100%',
                  height: '100%',
                }}
              />

              {/* Clear button — top-right corner (same as autograph) */}
              <button
                type="button"
                onClick={() => setValue('')}
                disabled={isLoading}
                className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 hover:bg-gray-100 text-gray-400 hover:text-gray-600 shadow-sm border border-gray-200 transition-colors disabled:opacity-40"
                title="Borrar nombre"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              {/* Landscape/Desktop Inline Input — bottom of canvas */}
              <div className="hidden landscape:flex lg:flex absolute bottom-5 left-3 right-3 items-center gap-2 bg-gray-50/80 rounded-lg px-3 py-1.5 border border-gray-200/60">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="text-gray-400 shrink-0">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
                <input
                  className="flex-1 bg-transparent text-sm text-gray-700 py-0.5 focus:outline-none placeholder:text-gray-400"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  placeholder="Escribe tu nombre completo..."
                  spellCheck={false}
                />
              </div>

              {/* Unified Debug Info Line */}
              <div className="absolute bottom-1.5 left-2 pointer-events-none select-none text-[8px] font-mono tabular-nums text-gray-300 flex items-center gap-1 opacity-70">
                <span>Powered by Cincel | {versionTag}</span>
                <span>-</span>
                <span>800:492</span>
                <span>-</span>
                <span>r {inkRatio.toFixed(4)}</span>
                <span>-</span>
                <span className={isTextEmpty ? "text-amber-500" : "text-green-500"}>
                  {isTextEmpty ? 'nil' : 'set'}
                </span>
              </div>
            </div>

            {/* Portrait Input Box (Always visible below canvas in mobile portrait) */}
            <div className="landscape:hidden lg:hidden w-full mt-4">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-gray-400">
                Nombre completo
              </label>
              <input
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-gray-400 placeholder:text-gray-400 transition-colors"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={(e) => e.target.select()}
                placeholder="Escribe tu nombre completo..."
                spellCheck={false}
              />
            </div>
          </div>
          {rightColumnContent}
        </div>
      )}
      {/* ---- 20MB warning ---- */}
      {showMessage && (
        <div className="mt-3 rounded-lg bg-amber-50 p-2 border border-amber-100">
          <p className="text-center text-[10px] text-amber-700 font-medium">
            {t(translations.DOCUMENT_PAGES.SIGN_MORE_THAN_20MB)}
          </p>
        </div>
      )}
    </Modal>
  );
};

type TabProps = React.PropsWithChildren<{
  className?: string;
  style?: React.CSSProperties;
  onClick?: (event: React.SyntheticEvent) => void;
}>;

function Tab({ className, children, ...props }: TabProps) {
  return (
    <button
      className={cn(
        'flex-1 border-b-2 pb-2 text-sm font-medium transition-colors duration-200',
        className
      )}
      {...props}>
      {children}
    </button>
  );
}
